import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import color from 'color';

const DefaultTheme = {
  colors: {
    black: '#110830',
    lightBlack: '#333333',
    lighterBlack: '#706e77',
    grey: '#9b9b9b',
    grey2: '#e7e7e7',
    grey3: '#f2f2f2',
    lightGrey: '#f8f8f8',
    darkGrey: '#a79fa9',
    deepLightGrey: '#d9d9d9',
    white: '#ffffff',
    blue: '#6552ff',
    blueHover: '#4d3bdf',
    darkBlue: '#160647',
    yellow: '#ffbf49',
    yellowHover: '#e68619',
    red: '#f75d5d',
    teal: '#47c998',
    green: '#28c33b',
    orange: '#f67c01',
    lightOrange: '#ffe0c0',
    textHighlightColor: '#6552ff',
    textHighlightColorDark: '#4d3bdf',
  },
  font: "'Inter', sans-serif",
  transition: 'all 0.2s',
  transitionIn: 'all 0.15s cubic-bezier(0.0, 0.0, 0.2, 1)',
  transitionOut: 'all 0.25s cubic-bezier(0.4, 0.0, 1, 1)',
  shadow: '0 1px 12px 0 rgba(0, 0, 0, 0.08)',
  shadowCard: '0 1px 2px 0 rgba(17, 13, 29, 0.26)',
  menuShadow:
    '0 0.6px 1.3px rgba(0, 0, 0, 0.017),0 1.4px 3.2px rgba(0, 0, 0, 0.024),0 2.6px 6px rgba(0, 0, 0, 0.03),0 4.7px 10.7px rgba(0, 0, 0, 0.036),0 8.8px 20.1px rgba(0, 0, 0, 0.043),0 21px 48px rgba(0, 0, 0, 0.06)',
};

function withCustomTheme() {
  const ThemeContext = React.createContext({});

  const placeholderThemeObj = {
    customBranding: false,
    theme: {
      iconURL: '',
      logoURL: '',
      primaryColor: '',
      secondaryColor: '',
      textHighlightColor: '',
    },
  };

  const ThemeProvider = ({ children }) => {
    const theme = useSelector(({ team: { teams } }) => {
      if (teams && teams.length) {
        const team = teams.find(
          ({ isDisabled, customBranding }) =>
            isDisabled === false && customBranding === true,
        );

        if (team && Object.keys(team.theme).length) {
          return team;
        }
      }

      return placeholderThemeObj;
    });

    /* eslint-disable indent */
    const value = theme.customBranding
      ? {
          ...DefaultTheme,
          colors: {
            ...DefaultTheme.colors,
            blue: theme.theme.primaryColor,
            blueHover: color(theme.theme.primaryColor).darken(0.1),
            orange: theme?.theme?.secondaryColor ?? DefaultTheme.colors.orange,
            textHighlightColor: theme.theme.textHighlightColor,
            textHighlightColorDark: color(
              theme.theme.textHighlightColor,
            ).darken(0.1),
          },
        }
      : DefaultTheme;
    /* eslint-enable indent */

    return (
      <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
  };

  ThemeProvider.propTypes = {
    children: PropTypes.node.isRequired,
  };

  const useTheme = () => useContext(ThemeContext);

  return {
    ThemeProvider,
    useTheme,
  };
}

export const { ThemeProvider, useTheme } = withCustomTheme();

export default DefaultTheme;
